/**
 * "Read my shops": a paired browser reads the whole shop on each storefront,
 * the listings tie to their items, and the live ones that couldn't be tied
 * for certain wait here for a person.
 */
import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ItemList, SeenListingRow, ShopItemRef, ShopRead } from "@sable/api-contract";
import { IconBox } from "../components/icons.tsx";
import { ErrorBlock, Loading } from "../components/ui.tsx";
import { t, tk } from "../i18n/index.ts";
import { request } from "../lib/api.ts";
import { money, shortDate } from "../lib/format.ts";
import { keys, useMarketplaces, useShops } from "../lib/queries.ts";
import { useCan, useWs, wsPath } from "../lib/workspace.tsx";

function Thumb({ src }: { src: string | null }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="mini">
      {src && !broken
        ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />
        : <div className="thumb-empty"><IconBox /></div>}
    </div>
  );
}

function ReadLine({ read }: { read: ShopRead | null }) {
  if (!read) return <span>{t("shops.never")}</span>;
  if (read.status === "requested") return <span>{t("shops.waiting")}</span>;
  if (read.status === "running") return <span>{t("shops.running", { count: read.seenSoFar })}</span>;
  if (read.status === "failed") return <span className="neg">{t("shops.failed", { error: read.error ?? "" })}</span>;
  if (read.status === "cancelled") return <span>{t("shops.cancelled")}</span>;
  const r = read.result;
  if (!r) return <span>{t("shops.doneAt", { when: shortDate(read.finishedAt) })}</span>;
  const linked = r.linked.listing + r.linked.sku + r.linked.title;
  return (
    <span>
      {t("shops.summary", { when: shortDate(read.finishedAt), seen: r.seen, linked, updated: r.updated, ended: r.ended, review: r.review })}
      {r.soldElsewhere > 0 && <> · {t("shops.soldElsewhere", { count: r.soldElsewhere })}</>}
      {!r.complete && <> · <span className="neg">{t("shops.incomplete")}</span></>}
    </span>
  );
}

function ItemPick({ item, onPick, busy, label }: { item: ShopItemRef & { score?: number }; onPick?: () => void; busy?: boolean; label?: string }) {
  const ws = useWs();
  return (
    <div className="shop-pick">
      <Thumb src={item.thumbUrl} />
      <div className="grow">
        <Link to={wsPath(ws, `/items/${item.id}`)} className="name">{item.title || t("inventory.untitled")}</Link>
        <div className="sub">{item.sku} · {tk("status", item.status)}{item.score !== undefined ? ` · ${t("shops.score", { score: item.score })}` : ""}</div>
      </div>
      {onPick && <button className="btn btn-sm" disabled={busy} onClick={onPick}>{label ?? t("shops.link")}</button>}
    </div>
  );
}

/** Finds another item to tie a listing to. */
function ItemSearch({ onPick, busy }: { onPick: (id: string) => void; busy: boolean }) {
  const ws = useWs();
  const [q, setQ] = useState("");
  const found = useQuery({
    queryKey: [...keys.items(ws.id), "shop-search", q],
    queryFn: () => request<ItemList>("/items", { workspace: ws.id, query: { q, status: "all", limit: 6 } }),
    enabled: q.trim().length >= 2,
  });
  return (
    <div className="stack">
      <input className="input" value={q} placeholder={t("shops.searchPlaceholder")} aria-label={t("shops.search")} onChange={(e) => setQ(e.target.value)} />
      {found.data?.items.map((i) => (
        <ItemPick key={i.id} busy={busy} onPick={() => onPick(i.id)}
          item={{ id: i.id, sku: i.sku, title: i.title, status: i.status, thumbUrl: i.thumbUrl }} />
      ))}
      {found.data && found.data.items.length === 0 && <p className="muted small">{t("shops.searchNone")}</p>}
    </div>
  );
}

function ReviewRow({ row, mpName, canTakeDown }: { row: SeenListingRow; mpName: string; canTakeDown: boolean }) {
  const ws = useWs();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [searching, setSearching] = useState(false);
  const path = `/shops/${row.marketplace}/listings/${encodeURIComponent(row.externalId)}`;

  async function act(action: "link" | "ignore" | "takedown", itemId?: string) {
    setBusy(true);
    setError(null);
    try {
      await request(`${path}/${action}`, { method: "POST", workspace: ws.id, body: itemId ? { itemId } : {} });
      void qc.invalidateQueries({ queryKey: keys.shops(ws.id) });
      void qc.invalidateQueries({ queryKey: keys.items(ws.id) });
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  }

  return (
    <div className="shop-row">
      <div className="shop-seen">
        <Thumb src={row.photoUrl} />
        <div className="grow">
          <div className="name">
            {row.url ? <a href={row.url} target="_blank" rel="noreferrer noopener">{row.title || row.externalId}</a> : row.title || row.externalId}
          </div>
          <div className="sub">
            {[mpName, row.brand, row.sizeLabel, row.priceMinor !== null ? money(row.priceMinor, row.currency) : null].filter(Boolean).join(" · ")}
          </div>
          <div className="small">{tk("shops.reason", row.reason)}</div>
        </div>
      </div>

      {row.item && <ItemPick item={row.item} />}
      {row.reason === "no_match" && row.suggestions.length > 0 && (
        <div className="stack">
          <div className="muted small">{t("shops.suggestions")}</div>
          {row.suggestions.map((s) => <ItemPick key={s.id} item={s} busy={busy} onPick={() => void act("link", s.id)} />)}
        </div>
      )}
      {searching && <ItemSearch busy={busy} onPick={(id) => void act("link", id)} />}
      {error ? <ErrorBlock error={error} /> : null}

      <div className="row-wrap">
        {row.reason === "item_gone" && canTakeDown && (
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void act("takedown")}>{t("shops.takeDown")}</button>
        )}
        {row.reason !== "item_gone" && !searching && (
          <button className="btn btn-sm" disabled={busy} onClick={() => setSearching(true)}>{t("shops.findItem")}</button>
        )}
        <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => void act("ignore")}>{t("shops.ignore")}</button>
      </div>
    </div>
  );
}

export function Shops() {
  const ws = useWs();
  const qc = useQueryClient();
  const can = useCan("listings.write");
  const mps = useMarketplaces();
  const q = useShops(ws.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mpName = (code: string) => mps.data?.find((m) => m.code === code)?.name ?? code;

  async function readAll(marketplaces?: string[]) {
    setBusy(true);
    setError(null);
    try {
      await request("/shops/read", { method: "POST", workspace: ws.id, body: marketplaces ? { marketplaces } : {} });
      void qc.invalidateQueries({ queryKey: keys.shops(ws.id) });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function cancel(id: string) {
    await request(`/shops/reads/${id}/cancel`, { method: "POST", workspace: ws.id });
    void qc.invalidateQueries({ queryKey: keys.shops(ws.id) });
  }

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorBlock error={q.error} onRetry={() => void q.refetch()} />;
  const { marketplaces, devices, review } = q.data;
  const online = devices.filter((d) => d.online);
  const open = marketplaces.some((m) => m.latest && (m.latest.status === "requested" || m.latest.status === "running"));

  return (
    <div className="stack-lg">
      <div className="page-head">
        <div>
          <h1>{t("shops.title")}</h1>
          <div className="sub">{t("shops.hint")}</div>
          <div className="small"><Link to={wsPath(ws, "/shops/report")}>{t("shops.reportLink")}</Link></div>
        </div>
        {can && (
          <button className="btn btn-primary" disabled={busy || open || marketplaces.length === 0} onClick={() => void readAll()}>
            {open ? t("shops.reading") : t("shops.readAll")}
          </button>
        )}
      </div>
      {error ? <ErrorBlock error={error} /> : null}

      <section className="card stack">
        {online.length === 0 && (
          <div className="banner">
            {t("shops.noBrowser")} <Link to={wsPath(ws, "/settings#extension")}>{t("shops.pairLink")}</Link>
          </div>
        )}
        {marketplaces.length === 0 ? <p className="muted small">{t("shops.noneReadable")}</p> : (
          <div className="list">
            {marketplaces.map((m) => {
              const signedIn = online.some((d) => d.signedIn[m.code]);
              const waiting = m.latest && (m.latest.status === "requested" || m.latest.status === "running");
              return (
                <div className="list-row" key={m.code}>
                  <div className="grow">
                    <div className="name">{mpName(m.code)}</div>
                    <div className="sub"><ReadLine read={m.latest} /></div>
                    <div className="sub">
                      {t("shops.linkedCount", { linked: m.linkedListings, total: m.listings })}
                      {online.length > 0 && !signedIn ? ` · ${t("shops.notSignedIn", { site: mpName(m.code) })}` : ""}
                    </div>
                  </div>
                  {can && (waiting
                    ? <button className="btn btn-sm btn-ghost" onClick={() => void cancel(m.latest!.id)}>{t("app.cancel")}</button>
                    : <button className="btn btn-sm" disabled={busy} onClick={() => void readAll([m.code])}>{t("shops.readOne")}</button>)}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="card stack">
        <div>
          <h2>{t("shops.reviewTitle", { count: review.length })}</h2>
          <p className="muted small">{t("shops.reviewHint")}</p>
        </div>
        {review.length === 0 ? <p className="muted small">{t("shops.reviewEmpty")}</p> : (
          <div className="list">
            {review.map((r) => (
              <ReviewRow key={`${r.marketplace}:${r.externalId}`} row={r} mpName={mpName(r.marketplace)}
                canTakeDown={marketplaces.find((m) => m.code === r.marketplace)?.canTakeDown ?? false} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
